<script lang="ts" setup>
import { ref, computed } from 'vue'
import { EnumStringFieldDefinition } from '@ironsight/form-builder'
import BulkEditComponent from '@/components/form-builder/BulkEditComponent.vue'
import { useDefaultFieldValues } from '@/components/form-builder/field-settings/useDefaultFieldValues'

interface Props {
  field: {
    selectableOptions: string[]
    isRadio: boolean
    defaultValue?: string | null
    uniqueKey: () => string
  }
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (
    e: 'modify-field',
    payload: {
      propertyKey: string
      fieldValue: string[] | boolean | string | null
      key: string
    },
  ): void
}>()

const showBulkEdit = ref(false)
const bulkEditText = ref('')

const showUiTypeToggle = computed(() => props.field instanceof EnumStringFieldDefinition)

const getEnumOptions = computed(() => {
  if (props.field.selectableOptions) {
    return props.field.selectableOptions.map((value) => ({
      text: value,
      value,
    }))
  }
  return []
})

function toggleUiType(isRadio: boolean) {
  emitChanges('isRadio', isRadio)
}

function addOption() {
  const updatedOptions = [...props.field.selectableOptions, '']
  emitChanges('selectableOptions', updatedOptions)
}

function removeOption(index: number) {
  const updatedOptions = [...props.field.selectableOptions]
  updatedOptions.splice(index, 1)
  bulkEditText.value = updatedOptions.join('\n')
  emitChanges('selectableOptions', updatedOptions)
}

function updateOptionText(text: string, index: number) {
  const updatedOptions = [...props.field.selectableOptions]
  updatedOptions[index] = text
  bulkEditText.value = updatedOptions.join('\n')
  emitChanges('selectableOptions', updatedOptions)
}

function initializeBulkEditText() {
  bulkEditText.value = props.field.selectableOptions.join('\n')
}

function toggleBulkEdit() {
  showBulkEdit.value = !showBulkEdit.value
}

function onBulkEditClicked() {
  initializeBulkEditText()
  toggleBulkEdit()
}

function applyBulkEditOptions(bulkOptions: string[]) {
  emitChanges('selectableOptions', bulkOptions)
  toggleBulkEdit()
}

const { showDefaultValue, toggleDefaultValue } = useDefaultFieldValues(props.field)

function emitChanges(propertyKey: string, fieldValue: string[] | boolean | string | null) {
  emit('modify-field', {
    propertyKey,
    fieldValue,
    key: props.field.uniqueKey(),
  })
}
</script>

<template>
  <vs-wrapper space="small">
    <v-radio-group
      v-if="showUiTypeToggle"
      :value="field.isRadio"
      label="Choose a visual type: "
      mandatory
      row
      @change="toggleUiType"
    >
      <v-radio :value="true" label="Radio"></v-radio>
      <v-radio :value="false" label="Dropdown"></v-radio>
    </v-radio-group>

    <template v-if="!showBulkEdit">
      <v-row v-for="(item, index) in field.selectableOptions" :key="index" align="center">
        <v-col class="mr-n4">
          <vs-text-input
            :value="item"
            label="Option label"
            @input="(newText) => updateOptionText(newText, index)"
          />
        </v-col>
        <v-col cols="auto">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-icon color="red lighten-2" v-bind="attrs" @click="removeOption(index)" v-on="on">
                delete
              </v-icon>
            </template>
            <span
              >Deleting or renaming an option that has dependencies will result in their
              deletion</span
            >
          </v-tooltip>
        </v-col>
      </v-row>
    </template>

    <bulk-edit-component
      v-if="showBulkEdit"
      :initial-text="bulkEditText"
      @apply="applyBulkEditOptions"
      @cancel="toggleBulkEdit"
    />

    <div class="mr-9 buttons-container">
      <vs-button
        v-if="!showBulkEdit"
        class="pa-3 mr-9 button-half-width"
        label="Add Option"
        type="secondary"
        @click="addOption"
      />
      <vs-button
        v-if="!showBulkEdit"
        class="pa-3 mr-9 button-half-width"
        label="Bulk Edit"
        type="secondary"
        @click="onBulkEditClicked"
      />
    </div>

    <v-checkbox
      :input-value="showDefaultValue"
      label="Default Value"
      @change="toggleDefaultValue"
    />

    <div v-if="showDefaultValue">
      <v-select
        v-model="field.defaultValue"
        :items="getEnumOptions"
        clearable
        label=""
        placeholder="Select default option"
      />
    </div>
  </vs-wrapper>
</template>

<style scoped>
.buttons-container {
  display: flex;
}

.button-half-width {
  flex: 1;
}
</style>
